<script setup lang="ts">
  import { computed } from 'vue';
  import { useModal } from 'vue-final-modal';

  import type { DocumentRevision } from '@/entities/Document';
  import { DocumentSendFormat } from '@/entities/DocumentSend';
  import { DocumentSelectionModal, useDocuments } from '@/features/Document';
  import { MAX_DOCUMENT_SEND_ATTACHMENTS } from '@/shared/config';
  import { formatDateToPtBR } from '@/shared/lib';
  import { ColoredButton, DatePicker, IconRadio, SelectInput } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import SelectDocumentIcon from '@/shared/assets/icons/SelectDocumentIcon.svg';
  import SendArrowIcon from '@/shared/assets/icons/SendArrow.svg';

  import type { SelectedGdDocument } from '../model/useDocumentSendCreateForm';
  import {
    DOCUMENT_SEND_MAX_GD_DOCUMENTS,
    MAX_SEND_TITLE_LENGTH,
    useDocumentSendCreateForm,
  } from '../model/useDocumentSendCreateForm';
  import { TOO_MANY_ATTACHMENTS_MESSAGE } from '../model/electronic-attachments';
  import {
    createPhysicalRecipientRow,
    type SelectedDocumentEntry,
  } from '../model/physicalRecipient';
  import DocumentSendAudienceSection from './DocumentSendAudienceSection.vue';
  import DocumentSendDocumentCard from './DocumentSendDocumentCard.vue';
  import DocumentSendElectronicRecipientSection from './DocumentSendElectronicRecipientSection.vue';
  import DocumentSendElectronicSection from './DocumentSendElectronicSection.vue';
  import DocumentSendGdDocumentTable from './DocumentSendGdDocumentTable.vue';

  const {
    addDocuments,
    canSelectMoreDocuments,
    canSubmitCombined,
    canSubmit,
    canSubmitElectronic,
    clientOptions,
    collaboratorDisplayName,
    confirmGdDocumentSelection,
    documentSelectionError,
    electronicClientOptions,
    electronicClientRecipients,
    electronicSupplierOptions,
    electronicSupplierRecipients,
    gdDocumentRevisionIds,
    gdDocumentsTotalBytes,
    handleSubmit,
    isDatePickerOpen,
    isDocumentSelectModalOpen,
    isLoadingElectronicRecipients,
    isSubmitting,
    localAttachmentCount,
    localFiles,
    localUrls,
    urlErrors,
    message,
    messageError,
    openSendDatePicker,
    positionOptions,
    recipientError,
    removeDocument,
    removeSelectedGdDocument,
    selectedAudienceType,
    selectedDocuments,
    selectedElectronicMode,
    selectedFormat,
    selectedGdDocuments,
    selectedPositionId,
    sendDate,
    senderPositionError,
    setElectronicClientRecipients,
    setElectronicSupplierRecipients,
    setLocalFiles,
    setLocalUrls,
    setUrlErrors,
    showsElectronicSection,
    showsPhysicalSection,
    submitCombinedSend,
    submitElectronicSend,
    supplierOptions,
    title,
    titleError,
    updateDocumentClientRecipients,
    updateDocumentSupplierRecipients,
    updateSendDate,
  } = useDocumentSendCreateForm();

  const { viewDocument } = useDocuments();

  const isElectronicOnly = computed(() => selectedFormat.value === DocumentSendFormat.ELECTRONIC);
  const isPhysicalOnly = computed(() => selectedFormat.value === DocumentSendFormat.PHYSICAL);
  const isCombinedSelected = computed(
    () => selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL
  );

  const includesClientRecipients = computed(
    () =>
      selectedAudienceType.value === 'CLIENT' || selectedAudienceType.value === 'CLIENT_SUPPLIER'
  );

  const includesSupplierRecipients = computed(
    () =>
      selectedAudienceType.value === 'SUPPLIER' || selectedAudienceType.value === 'CLIENT_SUPPLIER'
  );

  function toSelectedDocumentEntry(revision: DocumentRevision): SelectedDocumentEntry {
    return {
      documentRevisionId: revision.id,
      prefix: revision.document.prefix ?? '',
      title: revision.document.name ?? null,
      revision: revision.revision,
      originalFileName: revision.originalFileName ?? `documento_${revision.id}.pdf`,
      fileSizeBytes:
        typeof revision.fileSize === 'number'
          ? revision.fileSize
          : Number.parseInt(String(revision.fileSize ?? 0), 10) || 0,
      clientRecipients: includesClientRecipients.value ? [createPhysicalRecipientRow()] : [],
      supplierRecipients: includesSupplierRecipients.value ? [createPhysicalRecipientRow()] : [],
    };
  }

  function handleViewDocument(revision: DocumentRevision): void {
    viewDocument(revision.documentId.toString(), undefined, revision.id);
  }

  const mapSelectedGdDocumentToRevision = (document: SelectedGdDocument): DocumentRevision =>
    ({
      id: document.id,
      revision: document.revision,
      originalFileName: document.originalFileName,
      fileSize: document.fileSizeBytes,
      document: {
        prefix: document.prefix,
        name: document.title,
      },
    }) as DocumentRevision;

  const documentSelectionModal = useModal({
    component: DocumentSelectionModal,
    attrs: {
      title: 'Selecionar Documento',
      selectionMode: 'multiple',
      showSort: true,
      showAdvancedFilter: true,
      showViewAction: true,
      showSelectAll: true,
      maxSelections: DOCUMENT_SEND_MAX_GD_DOCUMENTS,
      selectionLimitMessage: `O número máximo de documentos selecionados é ${DOCUMENT_SEND_MAX_GD_DOCUMENTS}.`,
      onDemandRevisions: true,
      initialSelectedRevisionId: null,
      initialSelectedRevisionIds: [],
      initialSelectedRevisions: [],
      onClose: () => {
        documentSelectionModal.close();
      },
      'onSelect-multiple': (revisions: DocumentRevision[]) => {
        if (confirmGdDocumentSelection(revisions)) {
          documentSelectionModal.close();
        }
      },
      onView: handleViewDocument,
    },
  });

  const formatSendDate = (value: Date | null) => {
    const formattedDate = formatDateToPtBR(value);
    return formattedDate === '' ? 'Selecione uma data' : formattedDate;
  };

  const handleOpenDocumentSelection = () => {
    documentSelectionModal.patchOptions({
      attrs: {
        ...documentSelectionModal.options.attrs,
        initialSelectedRevisionIds: selectedGdDocuments.value.map((document) => document.id),
        initialSelectedRevisions: selectedGdDocuments.value.map(mapSelectedGdDocumentToRevision),
        selectionMode: 'multiple',
      },
    });
    documentSelectionModal.open();
  };
</script>

<template>
  <div
    class="flex flex-col gap-6 p-6 text-gray-07"
    data-testid="document-management-send-create-page"
  >
    <h1 class="text-left text-xl/7.5 font-semibold text-gray-07">
      Enviar
    </h1>

    <div class="divide-y divide-gray-02 overflow-x-auto rounded-2xl border border-gray-02 bg-white-01">
      <section class="flex flex-col gap-2 p-6">
        <label
          for="send-title"
          class="text-sm font-semibold text-gray-07"
        >
          Título:
          <span class="text-red-16">*</span>
        </label>
        <input
          id="send-title"
          v-model="title"
          type="text"
          :maxlength="MAX_SEND_TITLE_LENGTH"
          placeholder="Digite o título do envio"
          class="w-full rounded-lg border border-gray-02 bg-white-01 px-4 py-3 text-sm text-gray-07 focus:border-gray-07 focus:outline-none"
          data-testid="send-title-input"
        >
        <p
          v-if="titleError"
          class="text-xs text-red-16"
          data-testid="send-title-error"
        >
          {{ titleError }}
        </p>
      </section>

      <section class="flex flex-col gap-4 p-6">
        <span class="text-sm font-semibold text-gray-07">Formato do Documento:</span>
        <div
          class="flex flex-col gap-4 md:flex-row md:gap-6"
          data-testid="send-format-options"
        >
          <IconRadio
            v-model="selectedFormat"
            :value="DocumentSendFormat.ELECTRONIC"
            variant="small"
            label="Eletrônico"
            name="document-send-format"
          />
          <IconRadio
            v-model="selectedFormat"
            :value="DocumentSendFormat.PHYSICAL"
            variant="small"
            label="Físico"
            name="document-send-format"
          />
          <!-- Hidden on purpose: sends already stored as ELECTRONIC_PHYSICAL still depend on this format.
          <IconRadio
            v-model="selectedFormat"
            :value="DocumentSendFormat.ELECTRONIC_PHYSICAL"
            variant="small"
            label="Eletrônico/Físico"
            name="document-send-format"
          />
          -->
        </div>
      </section>

      <section
        v-if="selectedFormat"
        class="flex flex-col gap-4 p-6"
        data-testid="send-document-section"
      >
        <span class="text-sm font-semibold text-gray-07">
          {{ isPhysicalOnly ? 'Documento:' : 'Documento Eletrônico:' }}
        </span>

        <template v-if="showsPhysicalSection">
          <div
            v-if="selectedDocuments.length > 0"
            class="flex flex-col gap-3"
            data-testid="selected-documents-list"
          >
            <DocumentSendDocumentCard
              v-for="entry in selectedDocuments"
              :key="entry.documentRevisionId"
              :entry="entry"
              :client-options="clientOptions"
              :supplier-options="supplierOptions"
              :selected-audience-type="selectedAudienceType"
              @remove="removeDocument(entry.documentRevisionId)"
              @update:client-recipients="
                updateDocumentClientRecipients(entry.documentRevisionId, $event)
              "
              @update:supplier-recipients="
                updateDocumentSupplierRecipients(entry.documentRevisionId, $event)
              "
            />
          </div>

          <div
            class="max-w-md"
            data-testid="select-document-button-wrapper"
          >
            <ColoredButton
              text="Selecionar Documento"
              :icon="SelectDocumentIcon"
              icon-alt="Selecionar documento"
              :disabled="!canSelectMoreDocuments"
              @click="isDocumentSelectModalOpen = true"
            />
          </div>
        </template>

        <template v-else>
          <div
            class="max-w-md"
            data-testid="select-document-button-wrapper"
          >
            <ColoredButton
              text="Selecionar Documento"
              :icon="SelectDocumentIcon"
              icon-alt="Selecionar documento"
              :disabled="!canSelectMoreDocuments"
              @click="handleOpenDocumentSelection"
            />
          </div>

          <DocumentSendGdDocumentTable
            :documents="selectedGdDocuments"
            @remove="removeSelectedGdDocument"
          />
        </template>

        <p
          v-if="documentSelectionError"
          class="text-xs text-red-16"
          data-testid="send-document-error"
        >
          {{ documentSelectionError }}
        </p>
      </section>

      <DocumentSendAudienceSection
        v-if="isPhysicalOnly || isElectronicOnly || isCombinedSelected"
        v-model="selectedAudienceType"
        :error-message="isElectronicOnly || isCombinedSelected ? recipientError : ''"
      />

      <DocumentSendElectronicSection
        v-if="showsElectronicSection"
        v-model:selected-electronic-mode="selectedElectronicMode"
        v-model:message="message"
        :gd-attachment-count="gdDocumentRevisionIds.length"
        :gd-documents-total-bytes="gdDocumentsTotalBytes"
        :local-files="localFiles"
        :local-urls="localUrls"
        :url-errors="urlErrors"
        :message-error="messageError"
        @update:local-files="setLocalFiles"
        @update:local-urls="setLocalUrls"
        @update:url-errors="setUrlErrors"
      />

      <DocumentSendElectronicRecipientSection
        v-if="(isElectronicOnly || isCombinedSelected) && selectedAudienceType"
        :audience-type="selectedAudienceType"
        :client-recipients="electronicClientRecipients"
        :supplier-recipients="electronicSupplierRecipients"
        :client-options="electronicClientOptions"
        :supplier-options="electronicSupplierOptions"
        :error-message="recipientError"
        :is-loading="isLoadingElectronicRecipients"
        @update:client-recipients="setElectronicClientRecipients"
        @update:supplier-recipients="setElectronicSupplierRecipients"
      />

      <section class="flex flex-col gap-4 p-6">
        <div class="grid gap-4 lg:grid-cols-3">
          <div class="flex flex-col gap-2">
            <label
              for="sender-collaborator"
              class="text-sm font-semibold text-gray-07"
            >
              Colaborador:
            </label>
            <input
              id="sender-collaborator"
              :value="collaboratorDisplayName"
              type="text"
              disabled
              class="w-full cursor-not-allowed rounded-lg border border-gray-02 bg-gray-01 px-4 py-3 text-sm text-gray-07"
              data-testid="sender-collaborator-input"
            >
          </div>

          <div class="flex flex-col gap-2">
            <label
              for="sender-position"
              class="text-sm font-semibold text-gray-07"
            >Cargo:</label>
            <SelectInput
              id="sender-position"
              v-model="selectedPositionId"
              :options="positionOptions"
              label="Selecione o cargo"
              :disabled="positionOptions.length === 0"
              :use-teleport="true"
              data-testid="sender-position-select"
            />
            <p
              v-if="senderPositionError"
              class="text-xs text-red-16"
              data-testid="sender-position-error"
            >
              {{ senderPositionError }}
            </p>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-07">Data de envio:</label>
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 py-3 text-sm text-gray-07 focus:border-gray-07 focus:outline-none"
              data-testid="send-date-button"
              @click="openSendDatePicker"
            >
              <IconImage
                :src="CalendarIcon"
                alt="Calendário"
              />
              <span>{{ formatSendDate(sendDate) }}</span>
            </button>
          </div>
        </div>
      </section>
    </div>

    <div class="flex justify-end gap-6 pr-4 pb-1.5">
      <router-link
        to="/gestao-documentos/controle-envio"
        class="rounded-lg border border-gray-03 px-4 py-3 text-sm font-medium text-gray-03"
        data-testid="send-cancel-link"
      >
        Cancelar
      </router-link>

      <ColoredButton
        v-if="isElectronicOnly"
        text="Enviar"
        :icon="SendArrowIcon"
        icon-alt="Enviar"
        :disabled="!canSubmitElectronic || isSubmitting"
        data-testid="send-submit-button"
        @click="submitElectronicSend"
      />

      <ColoredButton
        v-else-if="isCombinedSelected"
        text="Enviar"
        :icon="SendArrowIcon"
        icon-alt="Enviar"
        :disabled="!canSubmitCombined || isSubmitting"
        data-testid="send-submit-button"
        @click="submitCombinedSend"
      />

      <ColoredButton
        v-else
        text="Enviar"
        :icon="SendArrowIcon"
        icon-alt="Enviar"
        :disabled="!canSubmit || isSubmitting"
        data-testid="send-submit-button"
        @click="handleSubmit"
      />
    </div>

    <DatePicker
      v-model:is-open="isDatePickerOpen"
      :selected-date="sendDate"
      :disable-future-dates="true"
      @update:selected-date="updateSendDate"
    />

    <DocumentSelectionModal
      v-if="isDocumentSelectModalOpen"
      selection-mode="multiple"
      title="Selecionar Documentos"
      table-title="Selecione os Documentos"
      :max-selections="
        isPhysicalOnly ? undefined : MAX_DOCUMENT_SEND_ATTACHMENTS - selectedDocuments.length - localAttachmentCount
      "
      show-sort
      show-advanced-filter
      show-view-action
      show-select-all
      :already-selected-revision-ids="
        selectedDocuments.map((document) => document.documentRevisionId)
      "
      :selection-limit-message="isPhysicalOnly ? undefined : TOO_MANY_ATTACHMENTS_MESSAGE"
      @close="isDocumentSelectModalOpen = false"
      @select-multiple="
        (revisions) => {
          addDocuments(revisions.map(toSelectedDocumentEntry));
          isDocumentSelectModalOpen = false;
        }
      "
      @view="handleViewDocument"
    />
  </div>
</template>
